import { useCallback, useEffect, useRef, useState } from "react";

// Toolkit imports
import {
  fetchExerciseDetails,
  fetchExerciseVariant,
  fetchLessonAttempt,
  fetchLessonResources,
  getAssessment,
} from "sj-ui-toolkit/src/api";
import { getExperimentClient, getLessonContentClient } from "sj-ui-toolkit/src/client";

const placeholderDetails = {
  lessonKey: "placeholder-lesson",
  id: null,
  type: "type",
  title: "Placeholder: Lesson Title",
  description: "Placeholder: This is a description for the lesson.",
  isBreakFix: false,
  hasExercise: false,
  completionType: "quiz", // default for video lessons
  hasCodeTests: false,
  isCtf: false,
  level: "Placeholder",
  duration: 0,
  showBeforeSurvey: false,
  showAfterSurvey: false,
  videoId: null,
  assessment: {
    id: null,
    answersCount: 0,
    questionsCount: 0,
    result: null,
  },
  nextLesson: null,
  sandbox: {
    instructions: {
      steps: [],
      tasks: [],
    },
    tabs: [],
    parameters: [],
  },
  resources: null,
};

/**
 * useLessonLoader Hook
 *
 * Custom hook to fetch and manage lesson details from multiple sources.
 * Combines data from Rails API and HackEdu content API for break/fix lessons.
 *
 * @param {string} lessonKey - The unique key identifying the lesson
 * @param {Object|null} tournamentContent - In a tournament context, the tournament lesson information
 * @param {Object|null} exerciseContent - Exercise details passed in for tournaments or exercise-only lessons
 * @returns {Object} Hook state and methods
 * @returns {boolean} isLoading - Whether lesson data is currently being fetched
 * @returns {boolean} isLessonLoaded - Whether lesson data has been successfully loaded
 * @returns {Object} lessonDetails - The combined lesson details object
 * @returns {Function} setLessonDetails - State setter for lesson details
 */
export function useLessonLoader(lessonKey, tournamentContent = null, exerciseContent = null) {
  const lessonContentClient = getLessonContentClient();
  const experimentClient = getExperimentClient();
  const [isLessonLoaded, setIsLessonLoaded] = useState(false);
  const [lessonDetails, setLessonDetails] = useState(placeholderDetails);
  const exerciseReservationRef = useRef(null);

  // Fetch lesson details from HackEdu content API (for break/fix lessons)
  const fetchHackEduLessonDetails = useCallback(async () => {
    console.log("Fetching HackEdu lesson details for lessonKey:", lessonKey);
    try {
      const details = await lessonContentClient.getLessonDetails(lessonKey);
      return details;
    } catch (error) {
      console.error("Error fetching lesson details:", error);
      return null;
    }
  }, [lessonContentClient, lessonKey]);

  // Fetch lesson details from Rails API
  const fetchRailsLessonDetails = useCallback(async () => {
    console.log("Fetching lesson details from Rails for lessonKey:", lessonKey);
    try {
      const data = await fetchLessonAttempt(lessonKey);

      const newLessonDetails = {
        id: data.lesson.id,
        type: data.type,
        title: data.lesson.name,
        description: data.lesson.description,
        level: data.levelName,
        duration: data.lesson.duration,
        isBreakFix: data.lesson.isBreakFixLesson,
        hasExercise: data.lesson.hasExperiment,
        videoId: data.lesson.videoId,
        since: data.lesson.since,
        events: {
          isConsumed: data.isConsumed,
          watchedVideo: data.watchedVideo,
          readTranscript: data.readTranscript,
        },
      };

      if (tournamentContent?.assessmentProps?.assessment) {
        newLessonDetails.assessment = tournamentContent.assessmentProps.assessment;
        newLessonDetails.question = tournamentContent.assessmentProps.question;
        newLessonDetails.showBeforeSurvey = false;
      } else {
        const assessment = await getAssessment(data.lesson.id);
        newLessonDetails.assessment = assessment.assessment;
        newLessonDetails.question = assessment.question;
        newLessonDetails.showBeforeSurvey = assessment.assessment.showBeforeSurvey;
        newLessonDetails.nextLesson = assessment.nextLesson;
      }

      newLessonDetails.experimentPassed = tournamentContent?.experimentPassed;
      newLessonDetails.lessonPassed = tournamentContent?.lessonPassed;

      return newLessonDetails;
    } catch (error) {
      console.error("Error fetching lesson details from Rails:", error);
      return null;
    }
  }, [lessonKey, tournamentContent]);

  // Reset loading state when lessonKey changes
  useEffect(() => {
    setLessonDetails(placeholderDetails);
    setIsLessonLoaded(false);
  }, [lessonKey]);

  // If we have an exercise, make the reservation now and set an interval to re-reserve every 10 minutes
  useEffect(() => {
    if (lessonDetails.hasExercise && lessonDetails.variant) {
      experimentClient.prepareTest(
        lessonDetails.variant.lessonKey,
        lessonDetails.variant.lessonVersion,
        lessonDetails.variant.currentVariant
      );

      exerciseReservationRef.current = setInterval(
        () => {
          experimentClient.prepareTest(
            lessonDetails.variant.lessonKey,
            lessonDetails.variant.lessonVersion,
            lessonDetails.variant.currentVariant
          );
        },
        1000 * 10 * 60
      );

      return () => clearInterval(exerciseReservationRef.current);
    }
  }, [lessonDetails.hasExercise]);

  // Load lesson details on mount or when lessonKey changes
  useEffect(() => {
    async function doLoad() {
      if (!lessonKey && !exerciseContent) {
        return;
      }

      let combinedLessonDetails = {};

      if (lessonKey) {
        // Load from Rails first
        const railsDetails = (await fetchRailsLessonDetails()) ?? {};
        combinedLessonDetails = { ...combinedLessonDetails, ...railsDetails };

        // Fetch additional details for break/fix lessons from svc-content
        if (combinedLessonDetails.isBreakFix) {
          const breakFixDetails = (await fetchHackEduLessonDetails()) ?? {};
          combinedLessonDetails = { ...combinedLessonDetails, ...breakFixDetails };
        }

        // Use exercise details from exerciseContent (i.e. tournament data) if available
        // otherwise fetch from Rails API
        if (combinedLessonDetails.hasExercise) {
          const exerciseDetails =
            exerciseContent?.experiment ?? (await fetchExerciseDetails(combinedLessonDetails.id));
          const variantDetails = await fetchExerciseVariant(exerciseDetails.experiment.id);
          exerciseDetails.variant = variantDetails;
          combinedLessonDetails = { ...combinedLessonDetails, ...exerciseDetails };
        }

        // Load resources
        try {
          const resources = await fetchLessonResources(combinedLessonDetails.id);
          if (resources?.length > 0) {
            combinedLessonDetails.resources = resources;
          }
        } catch (error) {
          console.error("Error fetching lesson resources:", error);
        }
      } else if (exerciseContent !== null) {
        // We're in an exercise-only context
        const { experiment, steps } = exerciseContent;
        const { name: title } = experiment;
        const variant = await fetchExerciseVariant(experiment.id);
        const staticDetails = {
          type: "Exercise",
          level: "Advanced",
          duration: 10,
          hasExercise: true,
        };
        combinedLessonDetails = { title, experiment, variant, steps, ...staticDetails };
      }

      // Update state with combined details
      setLessonDetails(combinedLessonDetails);
      setIsLessonLoaded(true);
    }

    if (!isLessonLoaded) {
      doLoad();
    }
  }, [
    isLessonLoaded,
    lessonKey,
    tournamentContent,
    exerciseContent,
    fetchRailsLessonDetails,
    fetchHackEduLessonDetails,
  ]);

  return {
    isLessonLoaded,
    lessonDetails,
    setLessonDetails,
  };
}
